import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Search, Building2, Users, HardHat, Briefcase, Eye, ChevronRight, Plus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const TenantsList = () => {
  const navigate = useNavigate();
  const { api } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await api.get('/api/super-admin/tenants');
        setTenants(res.data);
      } catch (err) {
        console.error('Failed to fetch tenants:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTenants();
  }, [api]);

  const filteredTenants = tenants.filter(t => {
    const matchesSearch = 
      t.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      t.client_id?.toLowerCase().includes(search.toLowerCase());
    const matchesTier = tierFilter === 'all' || t.tier === tierFilter;
    return matchesSearch && matchesTier;
  });

  const getTierColor = (tier) => {
    switch (tier) {
      case 'professional': return 'bg-[#ffd108] text-black';
      case 'advanced': return 'bg-blue-500 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'past_due': return 'bg-orange-500/20 text-orange-400';
      case 'cancelled': return 'bg-red-500/20 text-red-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Tenants</h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>{tenants.length} total tenants</p>
        </div>
        <Button 
          onClick={() => navigate('/super-admin/tenants/new')}
          className="bg-[#ffd108] text-black hover:bg-[#e6bc07]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Tenant
        </Button>
      </div>

      {/* Filters */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
              <Input
                placeholder="Search by company or client ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-10 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
              />
            </div>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className={`w-full sm:w-40 ${isDark ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                <SelectValue placeholder="Filter by tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tenants Table */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? 'border-slate-700 hover:bg-transparent' : 'border-gray-200 hover:bg-transparent'}>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Company</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Tier</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</TableHead>
                <TableHead className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-center`}><Users className="h-4 w-4 mx-auto" /></TableHead>
                <TableHead className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-center`}><HardHat className="h-4 w-4 mx-auto" /></TableHead>
                <TableHead className={`${isDark ? 'text-slate-400' : 'text-gray-500'} text-center`}><Briefcase className="h-4 w-4 mx-auto" /></TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTenants.map((tenant) => (
                <TableRow
                  key={tenant.client_id}
                  className={`${isDark ? 'border-slate-700 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'} cursor-pointer`}
                  onClick={() => navigate(`/super-admin/tenants/${tenant.client_id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 ${isDark ? 'bg-slate-700' : 'bg-gray-100'} rounded-lg`}>
                        <Building2 className="h-4 w-4 text-[#ffd108]" />
                      </div>
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.company_name}</p>
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{tenant.client_id}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getTierColor(tenant.tier)}>
                      {tenant.tier || 'basic'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(tenant.subscription_status)}>
                      {tenant.subscription_status || 'active'}
                    </Badge>
                  </TableCell>
                  <TableCell className={`text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.users_count}</TableCell>
                  <TableCell className={`text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.operators_count}</TableCell>
                  <TableCell className={`text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.jobs_count}</TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {tenant.created_at
                      ? formatDistanceToNow(new Date(tenant.created_at), { addSuffix: true })
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <ChevronRight className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantsList;
